import { useState } from 'react'
import { useProducts } from '../../hooks/useApi'
import { useGenerateCaption } from '../../hooks/useMarketing'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { ShareCenter } from '../../components/marketing/ShareCenter'
import { PROMO_TYPES, COPYWRITER_PLATFORMS, COPYWRITER_TONES } from '../../lib/marketing/constants'
import { Wand2, Copy } from 'lucide-react'
import { toast } from 'sonner'

export default function MarketingCopywriter() {
  const { data: products } = useProducts()
  const generateCaption = useGenerateCaption()

  const [productId, setProductId] = useState<number | ''>('')
  const [targetPromo, setTargetPromo] = useState(PROMO_TYPES[0].label)
  const [platform, setPlatform] = useState<string>(COPYWRITER_PLATFORMS[0])
  const [audience, setAudience] = useState('UMKM lokal, usia 25-45')
  const [tone, setTone] = useState<string>(COPYWRITER_TONES[0])
  const [result, setResult] = useState<{
    headline: string
    caption: string
    cta: string
    hashtags: string
    shortVersion: string
    longVersion: string
  } | null>(null)

  const handleGenerate = async () => {
    if (!productId) return
    const res = await generateCaption.mutateAsync({
      productId,
      targetPromo,
      platform,
      audience,
      tone,
    })
    setResult(res.content)
  }

  const copyField = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text)
    toast.success(`${label} disalin`)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI Copywriter</h1>
        <p className="text-gray-600 mt-1">Buat caption promosi profesional (1 AI Boost)</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Produk</label>
              <select className="w-full border rounded-lg px-3 py-2" value={productId} onChange={(e) => setProductId(Number(e.target.value) || '')}>
                <option value="">Pilih Produk</option>
                {products?.map((p: { id: number; name: string }) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Target Promosi</label>
              <select className="w-full border rounded-lg px-3 py-2" value={targetPromo} onChange={(e) => setTargetPromo(e.target.value)}>
                {PROMO_TYPES.map((p) => (
                  <option key={p.id} value={p.label}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Platform</label>
              <select className="w-full border rounded-lg px-3 py-2" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                {COPYWRITER_PLATFORMS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Target Audience</label>
              <input className="w-full border rounded-lg px-3 py-2" value={audience} onChange={(e) => setAudience(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Gaya Bahasa</label>
              <select className="w-full border rounded-lg px-3 py-2" value={tone} onChange={(e) => setTone(e.target.value)}>
                {COPYWRITER_TONES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleGenerate} disabled={!productId || generateCaption.isPending}>
              <Wand2 className="w-4 h-4 mr-2" />
              {generateCaption.isPending ? 'Membuat...' : 'Generate Caption (1 AI Boost)'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Hasil</h3>
            {result ? (
              <div className="space-y-4">
                {[
                  { label: 'Headline', value: result.headline },
                  { label: 'Caption', value: result.caption },
                  { label: 'Call To Action', value: result.cta },
                  { label: 'Hashtag', value: result.hashtags },
                  { label: 'Versi Pendek', value: result.shortVersion },
                  { label: 'Versi Panjang', value: result.longVersion },
                ].map(({ label, value }) => (
                  <div key={label} className="border rounded-lg p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-semibold text-gray-700">{label}</span>
                      <button type="button" onClick={() => copyField(value, label)} className="text-indigo-600 hover:text-indigo-800">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{value}</p>
                  </div>
                ))}
                <ShareCenter caption={`${result.caption}\n\n${result.hashtags}\n\n${result.cta}`} />
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Isi form dan klik Generate untuk membuat caption</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
